import { createApi } from "@reduxjs/toolkit/query/react";
import { DynamicBaseQuery } from "./baseQuery";
import { FluidIntakeRequest } from "./types/forms/requests/FluidIntakeRequest";
import { FluidIntakeReportResponse } from "./types/forms/responses/FluidIntakeReportResponse";
import { FluidIntakeHistoryResponse } from "./types/forms/responses/FluidIntakeHistoryResponse";
import { FluidIntakeHistoryRequest } from "./types/forms/requests/FluidIntakeHistoryRequest";
import { BowelMovementHistoryResponse } from "./types/forms/responses/BowelMovementHistoryResponse";
import { BowelMovementHistoryRequest } from "./types/forms/requests/BowelMovementHistoryRequest";
import { BowelMovementModel } from "./types/forms/models/BowelMovementModel";
import { BowelMovementRequest } from "./types/forms/requests/BowelMovementRequest";
import { FluidOutputHistoryResponse } from "./types/forms/responses/FluidOutputHistoryResponse";
import { FluidOutputHistoryRequest } from "./types/forms/requests/FluidOutputHistoryRequest";
import { FluidOutputModel } from "./types/forms/models/FluidOutputModel";
import { FluidOutputRequest } from "./types/forms/requests/FluidOutputRequest";
import { shiftHandoverApi } from "./shiftHandoverApi";

export const staticFormsApi = createApi({
  reducerPath: "staticFormsApi",
  baseQuery: DynamicBaseQuery,
  tagTypes: ["FluidEntries", "BowelEntries", "FluidOutputEntries"],
  endpoints: (builder) => ({
    createFluidIntake: builder.mutation<
      FluidIntakeReportResponse,
      FluidIntakeRequest
    >({
      query: ({ residentId, ...body }) => ({
        url: `/residents/${residentId}/fluid-intake`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["FluidEntries"],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => {});
        dispatch(shiftHandoverApi.util.invalidateTags(["ShiftHandover"]));
      },
    }),
    fluidIntakeHistory: builder.query<
      FluidIntakeHistoryResponse,
      FluidIntakeHistoryRequest
    >({
      query: (params) => ({
        url: `/residents/fluid-intake`,
        method: "GET",
        params: {
          ...params,
          filter: {
            ...params.filter,
          },
        },
      }),
      providesTags: ["FluidEntries"],
    }),
    bowelMovementHistory: builder.query<
      BowelMovementHistoryResponse,
      BowelMovementHistoryRequest
    >({
      query: (params) => ({
        url: `/residents/bowel-movements`,
        method: "GET",
        params: {
          ...params,
          filter: {
            ...params.filter,
          },
        },
      }),
      providesTags: ["BowelEntries"],
    }),
    createBowelMovement: builder.mutation<
      BowelMovementModel,
      BowelMovementRequest
    >({
      query: ({ residentId, ...body }) => ({
        url: `/residents/${residentId}/bowel-movements`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["BowelEntries"],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => {});
        dispatch(shiftHandoverApi.util.invalidateTags(["ShiftHandover"]));
      },
    }),
    fluidOutputHistory: builder.query<
      FluidOutputHistoryResponse,
      FluidOutputHistoryRequest
    >({
      query: (params) => ({
        url: `/residents/fluid-output`,
        method: "GET",
        params: {
          ...params,
          filter: {
            ...params.filter,
          },
        },
      }),
      providesTags: ["FluidOutputEntries"],
    }),
    createFluidOutput: builder.mutation<FluidOutputModel, FluidOutputRequest>({
      query: ({ residentId, ...body }) => ({
        url: `/residents/${residentId}/fluid-output`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["FluidOutputEntries"],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => {});
        dispatch(shiftHandoverApi.util.invalidateTags(["ShiftHandover"]));
      },
    }),
  }),
});
export const {
  useCreateFluidIntakeMutation,
  useFluidIntakeHistoryQuery,
  useLazyFluidIntakeHistoryQuery,
  useBowelMovementHistoryQuery,
  useLazyBowelMovementHistoryQuery,
  useCreateBowelMovementMutation,
  useFluidOutputHistoryQuery,
  useLazyFluidOutputHistoryQuery,
  useCreateFluidOutputMutation,
} = staticFormsApi;
